'use client';

import { useCrispIdentity } from '@/api/queries/crisp.queries';
import { AnalyticsEvent } from '@/shared/lib/analytics/events';
import { hideCrispLauncher, pushCrispIdentity, resetCrispSession } from '@/shared/lib/crisp';
import { useAuthStore } from '@/shared/stores/auth-store';
import Script from 'next/script';
import posthog from 'posthog-js';
import { useEffect, useRef } from 'react';

import '@/shared/lib/crisp/types.d.ts';

const IS_DEV = process.env.NODE_ENV === 'development';
const FORCE_DEV = process.env.NEXT_PUBLIC_CRISP_FORCE === '1';
const WEBSITE_ID = process.env.NEXT_PUBLIC_CRISP_WEBSITE_ID;
const ENABLED = !!WEBSITE_ID && (!IS_DEV || FORCE_DEV);

type CrispEventArg = unknown;
type CrispEventHandler = (...args: CrispEventArg[]) => void;

function onCrispEvent(event: string, handler: CrispEventHandler): void {
  if (typeof window === 'undefined') return;
  window.$crisp = window.$crisp ?? [];
  window.$crisp.push(['on', event, handler]);
}

function offCrispEvent(event: string): void {
  if (typeof window === 'undefined') return;
  window.$crisp = window.$crisp ?? [];
  window.$crisp.push(['off', event]);
}

function readEmailFromArgs(args: CrispEventArg[]): string | undefined {
  const value = args[0];
  return typeof value === 'string' ? value : undefined;
}

export function CrispProvider() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const identity = useCrispIdentity(isAuthenticated);
  const lastAppliedEmail = useRef<string | null>(null);

  useEffect(() => {
    if (!ENABLED) return;

    // Crisp's queue accepts pushes before the script loads — see
    // `pushCrisp` in @/shared/lib/crisp. The inline `<Script>` payload
    // preserves an existing `window.$crisp` array, so handlers we push
    // here survive into the queue that `client.crisp.chat/l.js` drains.
    hideCrispLauncher();

    onCrispEvent('chat:opened', () => {
      // Do NOT push chat:hide here. On Crisp, chat:hide hides the whole client
      // (not just the launcher), tearing down the just-opened panel — on mobile
      // that leaves a black full-screen shell with no conversation (FRONT-214).
      // Launcher visibility is owned by the CSS rule in globals.css, which hides
      // the idle "Open chat" state but keeps the "Close chat" bubble as the
      // mobile exit.
      posthog.capture(AnalyticsEvent.PLATFORM_CHAT_OPENED, {
        chat_provider: 'crisp',
        source: 'launcher_native',
      });
    });
    onCrispEvent('chat:closed', () => {
      // Crisp restores the launcher when the panel closes — hide again.
      hideCrispLauncher();
    });
    onCrispEvent('message:sent', () => {
      posthog.capture(AnalyticsEvent.PLATFORM_CHAT_MESSAGE_SENT, {
        chat_provider: 'crisp',
      });
    });
    onCrispEvent('user:email:changed', (...args: CrispEventArg[]) => {
      const email = readEmailFromArgs(args);
      posthog.capture(AnalyticsEvent.PLATFORM_CHAT_EMAIL_COLLECTED, {
        chat_provider: 'crisp',
        ...(email && { email }),
      });
      if (email) posthog.identify(email);
    });

    return () => {
      offCrispEvent('chat:opened');
      offCrispEvent('chat:closed');
      offCrispEvent('message:sent');
      offCrispEvent('user:email:changed');
    };
  }, []);

  useEffect(() => {
    if (!ENABLED) return;
    if (!isAuthenticated) {
      if (lastAppliedEmail.current !== null) {
        resetCrispSession();
        lastAppliedEmail.current = null;
      }
      return;
    }
    if (!identity.data) return;
    if (lastAppliedEmail.current === identity.data.email) return;

    pushCrispIdentity({
      email: identity.data.email,
      signature: identity.data.signature,
      ...(identity.data.nickname !== undefined && { nickname: identity.data.nickname }),
    });
    lastAppliedEmail.current = identity.data.email;
  }, [isAuthenticated, identity.data]);

  if (!ENABLED) return null;

  return (
    <Script id="crisp-loader" strategy="lazyOnload">
      {`window.$crisp=Array.isArray(window.$crisp)?window.$crisp:[];window.CRISP_WEBSITE_ID=${JSON.stringify(WEBSITE_ID)};(function(){var d=document;var s=d.createElement("script");s.src="https://client.crisp.chat/l.js";s.async=1;d.getElementsByTagName("head")[0].appendChild(s);})();`}
    </Script>
  );
}
